<template>
  <div class="conflated-domains-search">
    <!-- Loading state -->
    <div v-if="!isConfigReady" class="cnfltd-loading">
      <a-spin size="large" />
    </div>

    <!-- Config error state -->
    <div v-else-if="configError" class="cnfltd-error">
      <a-alert
        type="error"
        :message="configError"
        description="Failed to load shortcode configuration"
        show-icon
      />
    </div>

    <!-- App ready - render SearchForm -->
    <div v-else class="cnfltd-content">
      <h2 v-if="config?.title" class="cnfltd-title">{{ config.title }}</h2>
      
      <!-- T058: Wire up SearchForm submit event to call useDomainSearch.search() -->
      <SearchForm 
        v-if="config"
        :config="config"
        :is-searching="isSearching"
        @search="handleSearch"
      />
      
      <!-- T061: Conditionally render ErrorMessage when searchState is 'error' -->
      <ErrorMessage
        v-if="searchError"
        :message="searchError"
        :show-retry="true"
        @retry="retryLastSearch"
        @close="searchError = null"
      />
      
      <!-- T059: Conditionally render LoadingState when searchState is 'loading' -->
      <LoadingState v-if="isSearching" message="Searching for domains..." />
      
      <!-- T060, T062: Conditionally render SearchResults when searchState is 'results' -->
      <div ref="resultsSection">
        <SearchResults
          v-if="searchResults.length > 0 && !isSearching"
          :results="searchResults"
          :currency-symbol="config?.currency_symbol"
          :adding-domain="addingDomain"
          :has-more-tlds="hasMoreTlds"
          :is-loading-more="isLoadingMore"
          @add-to-cart="handleAddToCart"
          @load-more="handleLoadMore"
        />
        
        <!-- No results message -->
        <div v-if="!isSearching && lastSearch && searchResults.length === 0" class="cnfltd-no-results">
          <a-empty description="No domains found for your search. Try a different name or TLDs." />
        </div>
      </div>
      
      <!-- Cart error message -->
      <ErrorMessage
        v-if="cartError"
        title="Cart Error"
        :message="cartError"
        @close="cartError = null"
      />

      <!-- Cart success notification -->
      <a-alert
        v-if="cartSuccess"
        type="success"
        :message="cartSuccess"
        closable
        show-icon
        @close="() => cartSuccess = null"
        style="margin-bottom: 1rem;"
      />
      
      <!-- Debug: Show configuration in development -->
      <details v-if="isDevelopment" class="cnfltd-debug">
        <summary>Configuration (debug)</summary>
        <pre>{{ JSON.stringify(config, null, 2) }}</pre>
      </details>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'
import { useConfig } from './composables/useConfig'
import { useDomainSearch } from './composables/useDomainSearch'
import { useCart } from './composables/useCart'
import SearchForm from './components/SearchForm.vue'
import SearchResults from './components/SearchResults.vue'
import LoadingState from './components/LoadingState.vue'
import ErrorMessage from './components/ErrorMessage.vue'

// Props passed from index.ts during mount
interface Props {
  mountElementId: string
}

const props = defineProps<Props>()

// T057: Extract configuration from data-config attribute
const { config, error: configError, isReady } = useConfig(props.mountElementId)

// T057: Integrate useDomainSearch in App.vue to manage search state
const { isSearching, isLoadingMore, results: searchResults, error: searchError, search } = useDomainSearch()

// T064: Wire up "Add to Cart" button to useCart.addToCart()
const { addingDomain, cartError, cartSuccess, addToCart } = useCart()

const isConfigReady = computed(() => isReady())
const isDevelopment = computed(() => import.meta.env.DEV)

const resultsSection = ref<HTMLElement | null>(null)

// Store last search parameters for retry
const lastSearch = ref<{ domain: string; tlds: string[] } | null>(null)

// TLD pagination state
const tldBatchSize = 5 // Number of TLDs to load per batch
const currentTldIndex = ref(0) // Current position in the TLD list
const allTlds = ref<string[]>([]) // All available TLDs
const currentDomain = ref<string>('') // Current domain being searched

// Computed property to check if there are more TLDs to load
// This checks if there will be more AFTER the current batch finishes loading
const hasMoreTlds = computed(() => {
  return currentTldIndex.value < allTlds.value.length
})

// Get recommended TLDs first, then remaining TLDs
const getOrderedTlds = (tldPricing: any[]): string[] => {
  // Get recommended TLDs
  const recommended = tldPricing
    .filter(p => p.recommended)
    .map(p => p.tld)
  
  // Get all TLDs from pricing
  const allPricingTlds = tldPricing.map(p => p.tld)
  
  // Get remaining TLDs (not recommended)
  const remaining = allPricingTlds.filter(tld => !recommended.includes(tld))
  
  // Return recommended first, then all others
  return [...recommended, ...remaining]
}

// T058: Handle search event from SearchForm
const handleSearch = (domain: string, tlds: string[]) => {
  console.log('[App] handleSearch called with', tlds.length, 'TLDs')
  
  // Create ordered list: recommended TLDs first (if any), then all from tld_pricing
  let orderedTlds: string[]
  
  if (config.value?.tld_pricing && config.value.tld_pricing.length > 0) {
    // Use tld_pricing with recommended ones first
    orderedTlds = getOrderedTlds(config.value.tld_pricing)
    console.log('[App] Using', orderedTlds.length, 'TLDs from tld_pricing')
  } else {
    // Fallback to the provided TLDs list
    orderedTlds = tlds
    console.log('[App] Using', orderedTlds.length, 'TLDs from available_tlds')
  }
  
  console.log('[App] Ordered TLDs:', orderedTlds)
  
  // Store for pagination
  currentDomain.value = domain
  allTlds.value = orderedTlds
  currentTldIndex.value = 0
  
  // Get first batch
  const firstBatch = orderedTlds.slice(0, tldBatchSize)
  currentTldIndex.value = firstBatch.length
  
  console.log('[App] First batch:', firstBatch)
  console.log('[App] currentTldIndex set to:', currentTldIndex.value)
  
  lastSearch.value = { domain, tlds: firstBatch }
  
  // Search first batch of TLDs
  search(domain, firstBatch, false)
}

// Handle "Show more suggestions" button click
const handleLoadMore = async () => {
  if (!currentDomain.value) return
  
  console.log('[App] handleLoadMore called')
  console.log('[App] Current TLD index:', currentTldIndex.value)
  console.log('[App] All TLDs:', allTlds.value)
  console.log('[App] All TLDs length:', allTlds.value.length)
  
  // Get next batch of TLDs (up to batchSize or remaining)
  const endIndex = Math.min(currentTldIndex.value + tldBatchSize, allTlds.value.length)
  const nextBatch = allTlds.value.slice(currentTldIndex.value, endIndex)
  
  console.log('[App] Next batch of TLDs:', nextBatch)
  console.log('[App] Slice from', currentTldIndex.value, 'to', endIndex)
  
  if (nextBatch.length === 0) {
    console.log('[App] No more TLDs to fetch')
    return // No more TLDs to fetch
  }
  
  console.log('[App] Searching domain:', currentDomain.value, 'with TLDs:', nextBatch)
  console.log('[App] Current results count before search:', searchResults.value.length)
  
  // Search and append results (this sets isLoadingMore to true)
  await search(currentDomain.value, nextBatch, true)
  
  // Update index AFTER search completes
  currentTldIndex.value = endIndex
  console.log('[App] Search completed. Updated currentTldIndex to:', endIndex)
  console.log('[App] hasMoreTlds is now:', endIndex < allTlds.value.length)
}

// Retry last search
const retryLastSearch = () => {
  if (lastSearch.value) {
    search(lastSearch.value.domain, lastSearch.value.tlds)
  }
}

// T064: Handle add to cart event from ResultCard
const handleAddToCart = (domain: string, price: number) => {
  addToCart(domain, price)
}

// Watch for results changes to provide feedback and scroll
watch([isSearching, searchResults], ([newSearching, newResults]) => {
  // Scroll when search finishes and either results are present or it was a search which might show 'No results'
  if (!newSearching && (newResults.length > 0 || lastSearch.value)) {
    nextTick(() => {
      setTimeout(() => {
        if (resultsSection.value) {
          resultsSection.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 300)
    })
  }
})

// Watch for loading state changes
watch(isLoadingMore, (newValue: boolean) => {
  console.log('[App] isLoadingMore changed to:', newValue)
})

watch(hasMoreTlds, (newValue: boolean) => {
  console.log('[App] hasMoreTlds changed to:', newValue)
})
</script>

<style scoped>
.conflated-domains-search {
  padding: 1rem;
  max-width: 1400px;
  margin: 0 auto;
}

.cnfltd-loading {
  text-align: center;
  padding: 2rem;
}

.cnfltd-error {
  margin: 1rem 0;
}

.cnfltd-content {
  width: 100%;
}

.cnfltd-title {
  margin: 0 0 1.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  text-align: center;
}

.cnfltd-no-results {
  margin-top: 3rem;
  padding: 3rem;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.cnfltd-debug {
  margin-top: 2rem;
  padding: 1rem;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.cnfltd-debug pre {
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
  overflow-x: auto;
}
</style>
